import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocalStorageState } from 'ahooks';
import { AutocompletedField, AutocompletedFlags, AutofilledProfilePatchDto, CvProcessingStatus } from 'src/api/person-profile/types';
import { extractAutocompletedFields } from 'src/common/cv-upload';
import { Job, useJobQueue } from '../hooks/use-job-queue';
import { j2lStorageKeys } from '../common/j2l-cache';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { FileDataViewModel } from 'src/api/common';
import container from '../config/inversify-config';
import { PersonProfileFormApiService } from '../api/person-profile/person-profile-api';

type SetAutocompletedFields = (value: AutocompletedField[] | ((prev: AutocompletedField[] | undefined) => AutocompletedField[])) => void;

type SetAutocompletedFlags = (value: AutocompletedFlags | ((prev: AutocompletedFlags) => AutocompletedFlags)) => void;

type PreviousFieldsMap = Record<string, AutocompletedField[] | undefined>;

export enum ProfileAnalysisSource {
  Cv = 'Cv',
  Linkedin = 'Linkedin'
}

interface CvAnalysisContextType {
  autocompletedFields?: AutocompletedField[];
  setAutocompletedFields: SetAutocompletedFields;
  previousFieldsMap: PreviousFieldsMap;
  setPreviousFieldsForKey: (key: string, fields: AutocompletedField[] | undefined) => void;
  reviewedFlags: AutocompletedFlags;
  setReviewedFlags: SetAutocompletedFlags;
  handleCvUpload: (personProfileId: string, cvFile: FileDataViewModel | null, cvDocumentChanged: boolean) => Promise<boolean>;
  handleLinkedinUrl: (personProfileId: string, linkedInUrl: string, linkedinUrlChanged: boolean) => Promise<boolean>;
  cvProcessingStatus?: CvProcessingStatus;
  analysisSource: ProfileAnalysisSource | null;
}

const CvAnalysisContext = createContext<CvAnalysisContextType | undefined>(undefined);

export const CvAnalysisProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const userId = auth.user?.profile?.sub ?? 'temp';
  const { t } = useTranslation();

  const [cvProcessingStatus, setCvProcessingStatus] = useState<CvProcessingStatus | undefined>();
  const [analysisSource, setAnalysisSource] = useState<ProfileAnalysisSource | null>(null);

  const [autocompletedFields, setAutocompletedFields] = useLocalStorageState<AutocompletedField[]>(j2lStorageKeys.cvAutocompletedFields(userId), {
    defaultValue: []
  });
  const [previousFieldsMap, setPreviousFieldsMap] = useState<PreviousFieldsMap>({});

  const setPreviousFieldsForKey = (key: string, fields: AutocompletedField[] | undefined) => {
    setPreviousFieldsMap((prev) => ({
      ...prev,
      [key]: fields
    }));
  };

  const [reviewedFlags, setReviewedFlags] = React.useState<AutocompletedFlags>({});

  const onResult = (result: AutofilledProfilePatchDto) => {
    const fields = extractAutocompletedFields(result);

    if (fields.length === 0) {
      message.info(t('No se detectó información nueva en tu CV para completar el perfil.'), 5);
      setCvProcessingStatus(CvProcessingStatus.NoRelevantData);
    } else {
      message.success(t('Hemos autocompletado algunos campos de tu perfil con la información de tu CV.'), 5);
      setCvProcessingStatus(CvProcessingStatus.Success);
    }

    setAutocompletedFields(fields);
  };

  const onError = () => {
    message.error(t('Error al procesar el CV. Por favor, inténtalo de nuevo más tarde.'), 5);
    setCvProcessingStatus(CvProcessingStatus.Failed);
  };

  const { addJob, isProcessing } = useJobQueue<AutofilledProfilePatchDto>(j2lStorageKeys.cvAnalysisJobs(userId), onResult, onError);

  useEffect(() => {
    if (isProcessing) setCvProcessingStatus(CvProcessingStatus.InProgress);
  }, [isProcessing]);

  const triggerCvAnalysis = useCallback(
    (jobId: string) => {
      const maxAttempts = 24;
      const interval = 5000;

      const job: Job = {
        jobId,
        timestamp: Date.now(),
        maxAttempts,
        interval,
        attemptsMade: 0
      };

      addJob(job);
    },
    [addJob]
  );

  const formApiService = container.get<PersonProfileFormApiService>(PersonProfileFormApiService);

  const handleCvUpload = useCallback(
    async (personProfileId: string, cvFile: FileDataViewModel | null, cvDocumentChanged: boolean): Promise<boolean> => {
      // Skip if no changes or no file
      if (!cvDocumentChanged || !cvFile) return true;

      setAnalysisSource(ProfileAnalysisSource.Cv);

      try {
        const response = await formApiService.upsertCvDocument(personProfileId, {
          cvDocumentChanged: true,
          cvDocumentFile: cvFile
        });

        if (!response.isSuccess || !response.item?.jobId) {
          message.error(t(response.error ?? 'No hemos podido subir tu CV. Por favor inténtalo de nuevo.'), 5);
          setCvProcessingStatus(CvProcessingStatus.Failed);
          return false;
        }

        message.success(t('CV subido con éxito. Estamos analizando tu CV para autocompletar tu perfil.'), 5);

        triggerCvAnalysis(response.item.jobId);
        return true;
      } catch (error) {
        console.error('CV upload error:', error);
        message.error(t('No hemos podido subir tu CV. Por favor inténtalo de nuevo.'), 5);
        return false;
      }
    },
    [formApiService, t, triggerCvAnalysis]
  );

  const handleLinkedinUrl = useCallback(
    async (personProfileId: string, linkedInUrl: string, linkedinUrlChanged: boolean): Promise<boolean> => {
      if (!linkedinUrlChanged || !linkedInUrl) return true;

      setAnalysisSource(ProfileAnalysisSource.Linkedin);

      try {
        const response = await formApiService.autofillFromLinkedin(personProfileId, linkedInUrl);

        if (!response.isSuccess || !response.item?.jobId) {
          message.error(t(response.error ?? 'No hemos podido analizar tu perfil de Linkedin. Por favor inténtalo de nuevo.'), 5);
          setCvProcessingStatus(CvProcessingStatus.Failed);
          return false;
        }

        message.success(t('Estamos analizando tu perfil de Linkedin para autocompletar tu perfil.'), 5);

        triggerCvAnalysis(response.item.jobId);
        return true;
      } catch (error) {
        console.error('Autofill from inkedin url error:', error);
        message.error(t('No hemos podido analizar tu perfil de Linkedin. Por favor inténtalo de nuevo.'), 5);
        return false;
      }
    },
    [formApiService, t, triggerCvAnalysis]
  );

  const contextValue: CvAnalysisContextType = {
    autocompletedFields,
    setAutocompletedFields,
    previousFieldsMap,
    setPreviousFieldsForKey,
    handleCvUpload,
    handleLinkedinUrl,
    reviewedFlags,
    setReviewedFlags,
    cvProcessingStatus,
    analysisSource
  };

  return <CvAnalysisContext.Provider value={contextValue}>{children}</CvAnalysisContext.Provider>;
};

export const useCvAnalysisContext = (): CvAnalysisContextType => {
  const ctx = useContext(CvAnalysisContext);
  if (!ctx) throw new Error('useCvAnalysisContext must be used inside CvAnalysisProvider');
  return ctx;
};
